import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Pause, Play, Trash2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

interface AudioRecorderProps {
  onRecordingComplete: (file: File) => void;
  disabled?: boolean;
  className?: string;
}

type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped';

export const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onRecordingComplete,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = useCallback(async () => {
    setError(null);
    setPermissionDenied(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;
      audioChunksRef.current = [];

      // Use webm for better browser support, fallback to other formats
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // Create file from blob
        const extension = mimeType.includes('webm') ? 'webm' : 'mp4';
        const fileName = `recording_${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`;
        const file = new File([audioBlob], fileName, { type: mimeType });
        onRecordingComplete(file);
      };

      mediaRecorder.start(1000); // Collect data every second
      setRecordingState('recording');
      setDuration(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Failed to start recording:', err);
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setPermissionDenied(true);
        setError(
          t('stt.audioRecorder.permissionDenied', {
            defaultValue:
              'Microphone access denied. Please allow microphone access in your browser settings.',
          })
        );
      } else {
        setError(
          t('stt.audioRecorder.startError', {
            defaultValue: 'Failed to start recording. Please check your microphone.',
          })
        );
      }
    }
  }, [onRecordingComplete, t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState !== 'idle') {
      mediaRecorderRef.current.stop();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setRecordingState('stopped');
    }
  }, [recordingState]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.pause();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordingState('paused');
    }
  }, [recordingState]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'paused') {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
      setRecordingState('recording');
    }
  }, [recordingState]);

  const discardRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setRecordingState('idle');
    setDuration(0);
    audioChunksRef.current = [];
  }, [audioUrl]);

  const isRecording = recordingState === 'recording';
  const isPaused = recordingState === 'paused';
  const isStopped = recordingState === 'stopped';
  const isIdle = recordingState === 'idle';

  return (
    <div className={`w-full ${className}`}>
      <div className="border-2 border-dashed border-white/20 rounded-lg p-6 bg-black/20">
        <div className="flex flex-col items-center justify-center space-y-4">
          {/* Recording indicator */}
          {(isRecording || isPaused) && (
            <div className="flex items-center space-x-3">
              <div
                className={`w-4 h-4 rounded-full ${
                  isRecording ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'
                }`}
              />
              <span className="text-2xl font-mono font-semibold text-white/70">
                {formatDuration(duration)}
              </span>
            </div>
          )}

          {/* Audio preview when stopped */}
          {isStopped && audioUrl && (
            <div className="w-full max-w-md">
              <audio src={audioUrl} controls className="w-full" />
              <p className="text-sm text-white/50 text-center mt-2">
                {t('stt.audioRecorder.recordingReady', {
                  defaultValue: 'Recording ready for transcription',
                })}
              </p>
            </div>
          )}

          {/* Main controls */}
          <div className="flex items-center space-x-4">
            {isIdle && (
              <button
                onClick={startRecording}
                disabled={disabled}
                className={`
                  flex items-center space-x-2 px-6 py-3 rounded-full font-medium
                  transition-all duration-200
                  ${
                    disabled
                      ? 'bg-white/10 text-white/30 cursor-not-allowed'
                      : 'bg-red-500 text-white hover:bg-red-600 shadow-lg hover:shadow-xl'
                  }
                `}
              >
                <Mic className="w-5 h-5" />
                <span>
                  {t('stt.audioRecorder.startRecording', {
                    defaultValue: 'Start Recording',
                  })}
                </span>
              </button>
            )}

            {isRecording && (
              <>
                <button
                  onClick={pauseRecording}
                  className="flex items-center space-x-2 px-4 py-2 rounded-full bg-yellow-500 text-white hover:bg-yellow-600 transition-colors"
                >
                  <Pause className="w-5 h-5" />
                  <span>
                    {t('stt.audioRecorder.pause', {
                      defaultValue: 'Pause',
                    })}
                  </span>
                </button>
                <button
                  onClick={stopRecording}
                  className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gray-700 text-white hover:bg-gray-800 transition-colors"
                >
                  <Square className="w-5 h-5" />
                  <span>
                    {t('stt.audioRecorder.stop', {
                      defaultValue: 'Stop',
                    })}
                  </span>
                </button>
              </>
            )}

            {isPaused && (
              <>
                <button
                  onClick={resumeRecording}
                  className="flex items-center space-x-2 px-4 py-2 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors"
                >
                  <Play className="w-5 h-5" />
                  <span>
                    {t('stt.audioRecorder.resume', {
                      defaultValue: 'Resume',
                    })}
                  </span>
                </button>
                <button
                  onClick={stopRecording}
                  className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gray-700 text-white hover:bg-gray-800 transition-colors"
                >
                  <Square className="w-5 h-5" />
                  <span>
                    {t('stt.audioRecorder.stop', {
                      defaultValue: 'Stop',
                    })}
                  </span>
                </button>
              </>
            )}

            {isStopped && (
              <>
                <button
                  onClick={startRecording}
                  disabled={disabled}
                  className="flex items-center space-x-2 px-4 py-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  <Mic className="w-5 h-5" />
                  <span>
                    {t('stt.audioRecorder.recordAgain', {
                      defaultValue: 'Record Again',
                    })}
                  </span>
                </button>
                <button
                  onClick={discardRecording}
                  className="flex items-center space-x-2 px-4 py-2 rounded-full bg-white/10 text-white/70 hover:bg-white/20 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>
                    {t('stt.audioRecorder.discard', {
                      defaultValue: 'Discard',
                    })}
                  </span>
                </button>
              </>
            )}
          </div>

          {/* Instructions */}
          {isIdle && !permissionDenied && (
            <p className="text-sm text-white/50 text-center">
              {t('stt.audioRecorder.instructions', {
                defaultValue: 'Click to record audio from your microphone',
              })}
            </p>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-3 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}
    </div>
  );
};
